/**
 * React Error Boundary that catches rendering errors in page components.
 * Renders a GlassCard with error details while keeping sidebar/header functional.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { GlassCard } from "./glass-card";

interface ErrorBoundaryProps {
  readonly children: ReactNode;
}

interface ErrorBoundaryState {
  readonly hasError: boolean;
  readonly error: Error | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("[ErrorBoundary] Rendering error:", error, errorInfo);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const errorMessage = this.state.error?.message ?? "Unknown error";
      const truncated =
        errorMessage.length > 200
          ? `${errorMessage.slice(0, 200)}...`
          : errorMessage;

      const mailtoSubject = encodeURIComponent(
        `GDAPWIF Portal Error: ${truncated.slice(0, 100)}`,
      );
      const mailtoBody = encodeURIComponent(
        `Error: ${errorMessage}\n\nURL: ${window.location.href}\nTime: ${new Date().toISOString()}`,
      );

      return (
        <GlassCard className="max-w-lg mx-auto mt-12 text-center">
          <h2 className="text-h2 text-sc-navy font-display mb-3">
            Something went wrong
          </h2>
          <p className="text-body-sm font-mono text-sc-text-muted bg-sc-bg-subtle rounded-input p-3 mb-4 break-words">
            {truncated}
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={this.handleReload}
              className="px-4 py-2 rounded-btn bg-sc-primary text-white text-body-sm font-medium hover:opacity-90 transition-opacity"
            >
              Reload page
            </button>
            <a
              href={`mailto:support@switchconnect.com.au?subject=${mailtoSubject}&body=${mailtoBody}`}
              className="px-4 py-2 rounded-btn border border-sc-border text-sc-text-secondary text-body-sm font-medium hover:bg-sc-surface-hover transition-colors"
            >
              Report issue
            </a>
          </div>
        </GlassCard>
      );
    }

    return this.props.children;
  }
}
